import { useState } from "react";
import { deepDives } from "@/mocks/home";

export default function DeepDiveSection() {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [featured, ...rest] = deepDives;

  const toggle = (id: string) => setExpanded((prev) => (prev === id ? null : id));

  return (
    <section id="deep" className="bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <div className="reveal max-w-3xl">
          <span className="text-xs font-label font-medium tracking-widest text-primary-600">
            DEEP DIVES
          </span>
          <h2 className="mt-3 font-heading text-3xl md:text-4xl font-black text-foreground-950">
            深度爆料 · 把脉络讲清楚
          </h2>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
            不止给你结论，更把时间线、来源与关键节点一并摊开。点击卡片即可就地展开全文阅读。
          </p>
        </div>

        <article className="reveal mt-10 grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-8 rounded-lg overflow-hidden bg-background-100 border border-background-200">
          <div className="lg:col-span-6 w-full h-64 sm:h-80 lg:h-auto lg:min-h-[360px] overflow-hidden">
            <img
              src={featured.image}
              alt={`${featured.title}配图`}
              title={`${featured.title} · 吃瓜看料深度爆料`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="lg:col-span-6 p-5 md:p-8 flex flex-col justify-center">
            <div className="flex items-center flex-wrap gap-3">
              <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-medium">
                {featured.category}
              </span>
              <span className="text-xs text-foreground-500">{featured.updated}</span>
              <span className="text-xs text-foreground-500">· {featured.readTime}</span>
            </div>

            <h3 className="mt-4 font-heading text-2xl md:text-3xl font-black leading-snug text-foreground-950">
              {featured.title}
            </h3>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              {featured.excerpt}
            </p>

            {expanded === featured.id && (
              <div className="mt-5 space-y-3.5 border-l-2 border-primary-300 pl-4">
                {featured.body.map((para) => (
                  <p key={para} className="text-sm leading-relaxed text-foreground-700">
                    {para}
                  </p>
                ))}
              </div>
            )}

            <div className="mt-7 flex items-center justify-between gap-4">
              <span className="text-xs font-label text-foreground-500">{featured.author}</span>
              <button
                type="button"
                onClick={() => toggle(featured.id)}
                aria-expanded={expanded === featured.id}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
              >
                {expanded === featured.id ? "收起全文" : "展开全文"}
                <i
                  className={expanded === featured.id ? "ri-arrow-up-line" : "ri-arrow-down-line"}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </article>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {rest.map((item) => (
            <article
              key={item.id}
              className="reveal group flex flex-col rounded-lg overflow-hidden bg-background-100 border border-background-200 transition-colors hover:border-accent-300"
            >
              <div className="w-full h-44 overflow-hidden">
                <img
                  src={item.image}
                  alt={`${item.title}配图`}
                  title={`${item.title} · 吃瓜看料深度爆料`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col flex-1 p-5">
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                  <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-label font-medium">
                    {item.category}
                  </span>
                  <span className="text-xs text-foreground-500">{item.updated}</span>
                </div>

                <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">{item.excerpt}</p>

                {expanded === item.id && (
                  <div className="mt-4 space-y-3 border-l-2 border-accent-300 pl-4">
                    {item.body.map((para) => (
                      <p key={para} className="text-sm leading-relaxed text-foreground-700">
                        {para}
                      </p>
                    ))}
                  </div>
                )}

                <div className="mt-auto pt-5 flex items-center justify-between gap-3">
                  <span className="text-xs font-label text-foreground-500">{item.readTime}</span>
                  <button
                    type="button"
                    onClick={() => toggle(item.id)}
                    aria-expanded={expanded === item.id}
                    className="inline-flex items-center gap-1 text-sm font-medium text-foreground-800 transition-colors group-hover:text-accent-700 cursor-pointer"
                  >
                    {expanded === item.id ? "收起" : "展开全文"}
                    <i
                      className={expanded === item.id ? "ri-arrow-up-line" : "ri-arrow-down-line"}
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}